import {z} from 'zod'

export const CoordinationStatus = z.enum([
    'cancellation_requested' /*Cancelation in progress*/,
    'dates_dont_match',
    'host_contacted',
    'host_dates_shared',
    'host_not_ready',
    'host_unresponsive',
    'manual_coordination',
    'overdue_host_contacted',
    'photographer_not_available'
])
export type CoordinationStatus = z.infer<typeof CoordinationStatus>

export const HostAvailabilityDate = z.object({starting_time: z.string().min(1), ending_time: z.string().min(1)})
export type HostAvailabilityDate = z.infer<typeof HostAvailabilityDate>

export const AssignmentFlowType = z.enum([
    'Forced Match',
    'Loyalty Match',
    'Not Freelancer',
    'Best Creator',
    'Nearest Creator',
    'Multiple Creators',
    'First Accept',
    'Random Creator'
])
export type AssignmentFlowType = z.infer<typeof AssignmentFlowType>

export const Assignment = z.object({
    airbnb_link: z.string().url(),
    message_thread_url: z.string().url().nullable(),
    amount: z.number().nullish(),
    declined_type: z
        .enum(['hard_declined', 'soft_declined'])
        .nullable() /*hard_declined = declined*/ /*soft_declined = Opportunity expired*/,
    assets_rejected_reason: z
        .enum([
            'empty_folder',
            'empty_form',
            'exp_empty_folder',
            'exp_individual_assets',
            'exp_insufficient_assets',
            'exp_low_quality',
            'exp_too_much_assets',
            'exp_too_shaky',
            'exp_wrong_folder',
            'exp_wrong_ratio',
            'manual_explanation',
            'missing_assets',
            'not_enough_photos_form',
            'unmerged_brackets',
            'wrong_assets',
            'wrong_file_format',
            'wrong_listing_form'
        ])
        .nullable(),
    bonus_amount: z.coerce.number().nullish(),
    completed_at: z.string().nullable(),
    can_send_to_airbnb: z.boolean().nullish(),
    cancellation_date: z.string().min(1).nullable(),
    code: z.number().int(),
    distance_km: z.number().nullish(),
    distance_mile: z.number().nullish(),
    dates_dont_match_at: z.string().min(1).nullable(),
    entity: z.object({
        id: z.number().int().min(1),
        address: z.string().nullable(),
        bedrooms: z.number().nullable(),
        city: z.string().nullable(),
        country: z.string().nullable(),
        experience_duration: z.number().nullable(),
        external_id: z.string().min(1),
        timezone: z.string().nullish(),
        title: z.string().nullable()
    }),
    entity_type: z.object({
        name: z.string()
    }),
    expiration_date: z.string().min(1).nullable(),
    frame_final_folder_review_link: z.string().url().nullable(),
    host: z.object({
        id: z.number().int().min(1),
        first_name: z.string(),
        email: z.string().min(1),
        phone: z.string().nullable(),
        preferred_languages: z.string().nullable()
    }),
    host_contacted_at: z.string().min(1).nullable(),
    host_not_ready_at: z.string().min(1).nullable(),
    host_ready_at: z.string().min(1).nullable(),
    host_responsive_at: z.string().min(1).nullable(),
    host_unresponsive_at: z.string().min(1).nullable(),
    id: z.number().int().min(1),
    instructions: z.string().nullish(),
    is_bulk_assignment: z.boolean(),
    assignment_logic: AssignmentFlowType.nullable(),
    medias_to_upload: z.number().nullish(),
    notes: z.string().min(1).nullable(),
    assets_rejected_at: z.string().min(1).nullable(),
    old_date_shoot: z.string().min(1).nullable(),
    pdf_link: z.string().url().nullish(),
    photographer_not_available_at: z.string().min(1).nullable(),
    photographer_available_at: z.string().min(1).nullable(),
    portrait_bonus_amount: z.coerce.number().nullish(),
    quote: z
        .object({
            host_availability_dates: z.array(HostAvailabilityDate).max(8),
            amount: z.number().nullish()
        })
        .nullable(),
    reminder_at: z.string().min(1).nullable(),
    status: z.enum(['accepted', 'auto_cancelled', 'declined', 'hard_cancelled', 'pending', 'soft_cancelled']),
    task: z.object({
        id: z.number().int().positive(),
        inspection_id: z.string().nullable(),
        daryl_directory_id: z.string().nullable()
    }),
    task_category: z.object({
        id: z.number().int().positive(),
        name: z.string().min(1)
    }),
    task_media: z.object({
        id: z.number().int().positive(),
        name: z.enum(['photo', 'video', 'matterport', 'walkthrough'])
    }),
    task_service: z.object({
        id: z.number().int().positive(),
        name: z.string().min(1)
    }),
    task_status: z.object({
        coordination_status: CoordinationStatus.nullable(),
        status: z.enum([
            'accepted',
            'assets_in_review',
            'auto_cancelled',
            'completed',
            'hard_cancelled',
            'needs_more_assets',
            'overdue',
            'pending',
            'pending_assets',
            'pending_payment',
            'scheduled',
            'soft_cancelled',
            'waitlisted'
        ]),
        post_prod_status: z
            .enum([
                'initial_qa_failed',
                'needs_to_be_assigned',
                'final_qa_failed',
                'assigned',
                'initial_qa',
                'work_in_progress',
                'ready_for_final_qa',
                'ready_for_pre_upload',
                'ready_for_upload',
                'upload_in_progress',
                'upload_issue',
                'upload_completed',
                'host_full_rejection',
                'pending_host_review'
            ])
            .nullable()
    }),
    payments: z
        .array(
            z.object({
                payment_type: z.string().min(1),
                external_id: z.string().min(1),
                airbnb_link: z.string().url(),
                payout_photographer_currency_amount: z.number().nullable(),
                payout_amount: z.number(),
                payout_photographer_currency: z.string().nullable(),
                payment_date: z.string().nullable()
            })
        )
        .optional(),
    to_shoot_at: z.string().nullable(),
    total_amount: z.number().nullable(),
    travel_compensation: z.number().nullish(),
    photoshoot_checklist_pdf_link: z.string().url().nullish(),
    is_3d_scan: z.boolean(),
    is_paid_via_upwork: z.boolean().nullish()
})
export type Assignment = z.infer<typeof Assignment>

export const ServiceType = z.enum(['experience_video', 'home', 'matterport', 'review', 'walkthrough'])
export type ServiceType = z.infer<typeof ServiceType>

export const DeclineReason = z.object({
    id: z.number().int().min(1),
    key_name: z.string().min(1)
})

export const Space = z.object({
    id: z.number().int().min(1),
    configuration_space: z.object({
        raws_minimum_images: z.number()
    }),
    space: z.object({
        key_name: z.enum([
            'bathroom',
            'bedroom_1',
            'bedroom_2',
            'bedroom_3',
            'bedroom_4',
            'bedroom_5',
            'bedroom_6',
            'bedroom_7',
            'bedroom_8',
            'bedroom_9',
            'bedroom_10',
            'common_spaces',
            'dining_room',
            'exterior',
            'kitchen',
            'living_room',
            'office_study',
            'other',
            'outdoor_space',
            'pool_host_tub',
            // hotel spaces
            'hotel_exterior_day_dusk',
            'hotel_parking_pathway_entrance',
            'hotel_lobby_front_desk',
            'hotel_indoor_common_space',
            'hotel_outdoor_common_space',
            'hotel_private_room_suites',
            'hotel_bathroom',
            'hotel_wellness_space',
            'hotel_restaurant_bar',
            'hotel_professional_space',
            'hotel_accessibility_features'
        ])
    })
})
export type Space = z.infer<typeof Space>

export const Compensation = z.object({
    compensationKey: z.enum(['photo_session', 'travel', 'profile_photo', 'bonus', 'walkthrough', 'total']),
    currency: z.enum(['EUR', 'USD', 'GBP'])
})
export type Compensation = z.infer<typeof Compensation>

export const AssignmentUrlParams = z.object({
    code: z.coerce.number()
})

export const Offering = z.object({
    id: z.number().int().min(1),
    title: z.string().min(1),
    description: z.string().min(1),
    mainPhotoUrl: z.string().url().nullable()
})
export type Offering = z.infer<typeof Offering>
